import {
    getProviderMapping,
    listProviderMappings
} from './queries';
import {
    createProviderMapping as CREATE_PROVIDER_MAPPING,
    deleteProviderMapping as DELETE_PROVIDER_MAPPING,
} from './mutations';

import {GraphQLQuery,} from '@aws-amplify/api';
import {
    CreateProviderMapping,
    DeleteProviderMapping,
    GetProviderMapping,
    ListProviderMappings,
} from "../types.ts";
import { billingApiClient } from "../../billing-api-integration";
import {ListRecordsQuery} from "../../../types";
import {RecurringOption} from '../../prices/types';

const client = billingApiClient;

const normalizeFilter = (filter: { [key: string]: string }) =>
    Object.keys(filter).reduce((acc: { [key: string]: string }, key) => {
        if (filter[key] !== '' && filter[key] !== undefined) {
            acc[key] = filter[key];
        }
        return acc;
    }, {});

export const fetchRecords = async (query: ListRecordsQuery) => {
    const { filter, ...rest } = query;
    const transformedFilter = filter ? normalizeFilter(filter as { [key: string]: string }) : {};

    const response = await client.graphql<GraphQLQuery<ListProviderMappings>>({
        query: listProviderMappings,
        variables: {
            ...rest,
            ...(Object.keys(transformedFilter).length ? { filter: transformedFilter } : {})
        },
    });

    return {
        items: response.data?.listProviderMappings.items,
        nextToken: response.data?.listProviderMappings.nextToken,
    };
};

export const fetch = async (id: string) => {
    const response = await client.graphql<GraphQLQuery<GetProviderMapping>>({
        query: getProviderMapping,
        variables: {id: id},
    });
    return response.data?.getProviderMapping;
};

export const createProviderMapping = async (data: {
    entityType: string;
    localEntityId: string;
    billingProviderId: string;
}) => {
    let providerEntityId: string | null = null;

    // Check if billing provider is Stripe or PayPal
    const BillingProvidersService = await import('../../billing-providers/services/BillingProvidersService');
    const billingProvider = await BillingProvidersService.fetch(data.billingProviderId);

    if (billingProvider && billingProvider.name) {
        const providerName = billingProvider.name.toLowerCase();

        if (providerName.includes('stripe')) {
            // Create entity in Stripe first
            if (data.entityType === 'Product') {
                const ProductsService = await import('../../products/services/ProductsService');
                const StripeProductService = await import('../../stripe-api-integration/services/StripeProductService');

                const product = await ProductsService.fetch(data.localEntityId);
                if (product) {
                    const stripeProduct = await StripeProductService.createStripeProduct(data.localEntityId, {
                        name: product.name,
                        description: product.shortDescription || product.longDescription
                    });
                    providerEntityId = stripeProduct?.id || null;
                }
            } else if (data.entityType === 'Customer') {
                const CustomersService = await import('../../customers/services/CustomersService');
                const StripeCustomerService = await import('../../stripe-api-integration/services/StripeCustomerService');

                const customer = await CustomersService.fetch(data.localEntityId);
                if (customer) {
                    const stripeCustomer = await StripeCustomerService.createStripeCustomer(data.localEntityId, {
                        name: customer.name,
                        description: customer.description,
                        email: customer.contactInfo?.email
                    });
                    providerEntityId = stripeCustomer?.id || null;
                }
            } else if (data.entityType === 'Price') {
                const PricesService = await import('../../prices/services/PricesService');
                const StripePriceService = await import('../../stripe-api-integration/services/StripePriceService');

                const price = await PricesService.fetch(data.localEntityId);
                if (!price) {
                    throw new Error('Price not found');
                }

                // Get product provider mapping
                const productMappingResponse = await fetchRecords({
                    filter: {
                        entityType: 'Product',
                        localEntityId: price.productId,
                        billingProviderId: data.billingProviderId
                    }
                });

                const productMapping = productMappingResponse?.items?.[0];
                if (!productMapping?.providerEntityId) {
                    throw new Error('Product must have a Stripe provider mapping first');
                }

                const intervalMap: Record<string, string> = {
                    'monthly': 'month',
                    'annually': 'year',
                    'daily': 'day',
                    'weekly': 'week'
                };

                const interval = intervalMap[price.billingPeriod?.toLowerCase() || 'monthly'] || 'month';

                const stripePriceData = {
                    currency: 'usd',
                    product: productMapping.providerEntityId,
                    unitAmount: Math.round((price.amount || 0) * 100),
                    active: price.priceStatus === 'Active',
                    nickname: price.description,
                    recurring: price.recurringOption === RecurringOption.Yes ? {
                        interval: interval
                    } : undefined
                };

                const stripePrice = await StripePriceService.createStripePrice(stripePriceData);
                providerEntityId = stripePrice?.id || null;
            }
        } else if (providerName.includes('paypal')) {
            // Create entity in PayPal
            if (data.entityType === 'Product') {
                const ProductsService = await import('../../products/services/ProductsService');
                const PayPalProductService = await import('../../paypal-api-integration/services/PayPalProductService');

                const product = await ProductsService.fetch(data.localEntityId);
                if (product) {
                    const paypalProduct = await PayPalProductService.createPayPalProduct({
                        name: product.name,
                        description: product.shortDescription || product.longDescription,
                        type: 'SERVICE',
                        category: 'SOFTWARE'
                    });
                    providerEntityId = paypalProduct?.id || null;
                }
            } else if (data.entityType === 'Price') {
                const PricesService = await import('../../prices/services/PricesService');
                const PayPalPlanService = await import('../../paypal-api-integration/services/PayPalPlanService');

                const price = await PricesService.fetch(data.localEntityId);
                if (!price) {
                    throw new Error('Price not found');
                }

                // Get product provider mapping
                const productMappingResponse = await fetchRecords({
                    filter: {
                        entityType: 'Product',
                        localEntityId: price.productId,
                        billingProviderId: data.billingProviderId
                    }
                });

                const productMapping = productMappingResponse?.items?.[0];
                if (!productMapping?.providerEntityId) {
                    throw new Error('Product must have a PayPal provider mapping first');
                }

                const intervalUnitMap: Record<string, string> = {
                    'monthly': 'MONTH',
                    'annually': 'YEAR',
                    'daily': 'DAY',
                    'weekly': 'WEEK'
                };

                const intervalUnit = intervalUnitMap[price.billingPeriod?.toLowerCase() || 'monthly'] || 'MONTH';

                const paypalPlan = await PayPalPlanService.createPayPalPlan({
                    productId: productMapping.providerEntityId,
                    name: price.description || `Plan for ${price.id}`,
                    description: price.description,
                    status: 'ACTIVE',
                    billingCycles: [{
                        frequency: {
                            intervalUnit: intervalUnit,
                            intervalCount: 1
                        },
                        tenureType: 'REGULAR',
                        sequence: 1,
                        totalCycles: 0, // 0 means infinite
                        pricingScheme: {
                            fixedPrice: {
                                currencyCode: 'USD',
                                value: (price.amount || 0).toString()
                            }
                        }
                    }]
                });
                providerEntityId = paypalPlan?.id || null;
            }
        }
    }

    // Create the provider mapping
    const response = await client.graphql<GraphQLQuery<CreateProviderMapping>>({
        query: CREATE_PROVIDER_MAPPING,
        variables: {
            input: {
                entityType: data.entityType,
                localEntityId: data.localEntityId,
                billingProviderId: data.billingProviderId,
                providerEntityId: providerEntityId || '',
                syncStatus: providerEntityId ? 'Synced' : 'Pending',
                lastSyncAt: providerEntityId ? new Date().toISOString() : null
            }
        }
    });

    return response.data?.createProviderMapping;
};

export const deleteProviderMappingWithSync = async (mapping: {
    id: string;
    billingProviderId: string;
    providerEntityId?: string;
    entityType: string;
    localEntityId: string;
}) => {
    // Delete from billing provider first if it exists
    if (mapping.providerEntityId) {
        const BillingProvidersService = await import('../../billing-providers/services/BillingProvidersService');
        const billingProvider = await BillingProvidersService.fetch(mapping.billingProviderId);

        if (billingProvider && billingProvider.name) {
            const providerName = billingProvider.name.toLowerCase();

            try {
                if (providerName.includes('stripe')) {
                    if (mapping.entityType === 'Product') {
                        const StripeProductService = await import('../../stripe-api-integration/services/StripeProductService');
                        await StripeProductService.deleteStripeProduct(mapping.providerEntityId, mapping.localEntityId);
                    } else if (mapping.entityType === 'Customer') {
                        const StripeCustomerService = await import('../../stripe-api-integration/services/StripeCustomerService');
                        await StripeCustomerService.deleteStripeCustomer(mapping.providerEntityId);
                    } else if (mapping.entityType === 'Price') {
                        const StripePriceService = await import('../../stripe-api-integration/services/StripePriceService');
                        await StripePriceService.deleteStripePrice(mapping.providerEntityId);
                    }
                } else if (providerName.includes('paypal')) {
                    // PayPal doesn't support deletion of products via API
                    // Products need to be archived/deactivated manually in PayPal dashboard
                    console.warn(`PayPal entity ${mapping.providerEntityId} should be manually archived in PayPal dashboard`);
                }
            } catch (providerError: unknown) {
                // Continue with local deletion even if provider deletion fails
                console.warn(`Provider deletion failed for ${mapping.providerEntityId}, continuing with local deletion:`, providerError);
            }
        }
    }

    // Delete local mapping
    const response = await client.graphql<GraphQLQuery<DeleteProviderMapping>>({
        query: DELETE_PROVIDER_MAPPING,
        variables: {
            input: { id: mapping.id }
        }
    });

    return response.data?.deleteProviderMapping;
};
